import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { LoadingSpinner, Modal } from '../../../components/UI';
import toast from 'react-hot-toast';

const empty = { role_name: '', role_code: '', description: '', is_active: 1 };

export default function RbacRoles() {
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/rbac/roles').then(r => setRoles(r.roles || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openAdd  = () => { setForm(empty); setEditing(null); setModal(true); };
  const openEdit = (r) => { setForm({ ...r }); setEditing(r); setModal(true); };
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/rbac/roles/${editing.id}`, form);
        toast.success('Role updated');
      } else {
        await api.post('/rbac/roles', form);
        toast.success('Role created');
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await api.delete(`/rbac/roles/${id}`);
      toast.success('Role deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cannot delete — role may be in use');
    } finally { setDeleting(null); }
  };

  const SYSTEM_ROLES = ['admin', 'developer', 'citizen', 'related_dept', 'final_dept'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🔒 Role Management</h1>
          <p className="text-sm text-gray-500 mt-1">Create and manage system roles</p>
        </div>
        <button onClick={openAdd} className="btn-primary">+ Add Role</button>
      </div>

      <div className="card overflow-hidden p-0">
        {loading ? <LoadingSpinner className="py-12" /> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Role Name</th>
                <th>Role Code</th>
                <th>Description</th>
                <th>Status</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r, i) => (
                <tr key={r.id}>
                  <td className="text-xs text-gray-400">{i + 1}</td>
                  <td className="font-semibold text-gray-800">{r.role_name}</td>
                  <td><code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{r.role_code}</code></td>
                  <td className="text-sm text-gray-500">{r.description || '—'}</td>
                  <td>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      r.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                    }`}>
                      {r.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      SYSTEM_ROLES.includes(r.role_code)
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {SYSTEM_ROLES.includes(r.role_code) ? '🔐 System' : '✏️ Custom'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openEdit(r)}
                        className="text-xs bg-yellow-50 text-yellow-600 border border-yellow-200 px-2 py-1 rounded-lg hover:bg-yellow-100">
                        ✏️ Edit
                      </button>
                      {!SYSTEM_ROLES.includes(r.role_code) && (
                        <button onClick={() => handleDelete(r.id)}
                          disabled={deleting === r.id}
                          className="text-xs bg-red-50 text-red-600 border border-red-200 px-2 py-1 rounded-lg hover:bg-red-100 disabled:opacity-40">
                          {deleting === r.id ? '...' : '🗑️ Delete'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal isOpen={modal} onClose={() => setModal(false)}
        title={editing ? 'Edit Role' : 'Add New Role'} size="md">
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="form-label">Role Name <span className="text-red-500">*</span></label>
            <input className="form-input" value={form.role_name}
              onChange={e => f('role_name', e.target.value)} required
              placeholder="e.g. Head Officer" />
          </div>
          <div>
            <label className="form-label">Role Code <span className="text-red-500">*</span></label>
            <input className="form-input" value={form.role_code}
              onChange={e => f('role_code', e.target.value.toLowerCase().replace(/\s+/g,'_'))}
              required placeholder="e.g. head_officer"
              disabled={!!editing && SYSTEM_ROLES.includes(editing.role_code)} />
            <p className="text-xs text-gray-400 mt-1">Lowercase, no spaces. Used in code.</p>
          </div>
          <div>
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={2} value={form.description || ''}
              onChange={e => f('description', e.target.value)}
              placeholder="Brief description of this role" />
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-select" value={form.is_active}
              onChange={e => f('is_active', parseInt(e.target.value))}>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" className="btn-secondary" onClick={() => setModal(false)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}